// @flow
import React, { useMemo, useEffect, useState } from 'react';
import map from 'ramda/es/map';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useTranslation } from 'react-i18next';

import { toFixedValueCurrency, makeCnbyValue } from 'app/common/lib/toFixedValue';
import Bill from 'app/common/ui/Bill';
import Tooltip from 'app/common/ui/Tooltip';
import { useCurrencySign } from 'app/common/components-next/currency';

import { type DayDetails } from '../HistoryApi';
import styles from './ItemDetails.module.scss';;


const cnbyValue = makeCnbyValue(styles.negative, styles.positive);

type Props = {
  details: DayDetails,
  date?: string,
  timezone: string,
}

const ItemDetails = ({ details, date, timezone }: Props) => {
  const { t } = useTranslation('finances');
  const currency = useCurrencySign();

  const [showItems, setShowItems] = useState(false);

  const mDate = moment(date);
  const header = mDate.isValid()
    ? t('balance_detail_title', { date: mDate.format('DD.MM.YYYY') })
    : 'Детализация финансов';

  const { at_start: startSum, at_end: endSum, details: detailsItems } = details;
  const dayStartSum = toFixedValueCurrency(startSum, false, currency);
  const dayEndSum = toFixedValueCurrency(endSum, false, currency);

  const items = useMemo(() => (detailsItems
    ? map(({ bill_pdf_url: billUrl, datetime, sum, title, type }) => ({
      billUrl,
      datetime,
      time: datetime ? moment(datetime).tz(timezone).format('HH:mm') : '',
      sum: toFixedValueCurrency(sum, false, currency),
      title,
      type: type === 'add' ? t('refill') : t('write_off'),
    }), detailsItems)
    : []
  ), [detailsItems, timezone, t, currency]);

  useEffect(() => {
    setShowItems(false);

    // нужен таймер, иначе Scrollbars не обновляется
    setTimeout(() => {
      setShowItems(true);
    }, 60);

    // и не нужен скролинг в body
    const body = document.getElementsByTagName('body')[0];
    const origW = body.offsetWidth;
    body.classList.add(styles.noscroll);
    const scrollW = body.offsetWidth - origW;
    body.style.paddingRight = `${scrollW}px`;

    // возврат скролинга
    return () => {
      const body2 = document.getElementsByTagName('body')[0];
      body2.classList.remove(styles.noscroll);
      body2.style.paddingRight = '0';
    };
  }, [items]);

  return (
    <div className={styles.root}>
      <h3 className={styles.header}>
        <span>{header}</span>
      </h3>
      <div className={styles.balances}>
        <div className={styles.balanceItem}>
          <div className={styles.balancesHeader}>{t('balance_detail_start_day_title')}</div>
          <div className={classNames(styles.balancesSum, cnbyValue(dayStartSum))}>
            {startSum ? dayStartSum : '—'}
          </div>
        </div>
        <div className={styles.balanceItem}>
          <div className={styles.balancesHeader}>{t('balance_detail_end_day_title')}</div>
          <div className={classNames(styles.balancesSum, cnbyValue(dayEndSum))}>
            {endSum ? dayEndSum : '—'}
          </div>
        </div>
      </div>
      {showItems
        && <Scrollbars className={styles.scrollbars}>
          {items.map(({ datetime, type, time, sum, title, billUrl }, i) => (
            <div
              key={`${datetime}_${i}`} // eslint-disable-line react/no-array-index-key
              className={styles.item}
            >
              <div className={styles.itemHeader}>
                <div className={styles.itemType}>{type}</div>
                <div className={styles.itemTime}>{time}</div>
                <div>
                  <Tooltip text={t('bill_tooltip')}>
                    <Bill link={billUrl} />
                  </Tooltip>
                </div>
                <div className={classNames(styles.itemSum, cnbyValue(sum))}>{sum}</div>
              </div>
              <div className={styles.itemTitle}>
                {title}
              </div>
            </div>
          ))}
          <div className={styles.space} />
        </Scrollbars>}
    </div>
  );
};

export default ItemDetails;
